import DisclosureToggle from 'tio-ui/components/disclosure-toggle';
import Component from '@glimmer/component';
import type { TOC } from '@ember/component/template-only';
import { uniqueId } from '@ember/helper';
import { tracked } from '@glimmer/tracking';
import t from 'ember-intl/helpers/t';
import Collapsible from 'tio-ui/components/collapsible';
import Text from 'tio-ui/components/text';
import AppPageProsVsCons from '../app/page/pros-vs-cons';
import {
  planCopyKeys,
  PLAN_COPY_MISSING_KEY,
  PSLF_COPY,
  TLF_COPY,
  type PlanCopyKeys,
} from '../../utils/strategy-finder-0726';

// One copy block: subtitle, "best for", pros/cons, and enrollment instructions,
// all resolved from translation keys. When the subtitle resolves to the shared
// "missing" marker the whole plan has no copy yet (e.g. RAP, the new Standard
// plan), so render a single visible marker instead of empty sections.
const CopyBlock: TOC<{
  Args: { copy: PlanCopyKeys; heading?: string };
  Element: HTMLDivElement;
}> = <template>
  <div class="space-y-3" ...attributes>
    {{#if @heading}}
      <Text @weight="semibold" class="block">{{t @heading}}</Text>
    {{/if}}
    {{#if (eq @copy.subtitle PLAN_COPY_MISSING_KEY)}}
      <p class="text-sm tio-fg-danger" data-test-sf-0726-plan-copy-missing>
        {{t @copy.subtitle}}
      </p>
    {{else}}
      <Text @tag="p" @variant="muted">{{t @copy.subtitle}}</Text>
      <div>
        <Text @weight="semibold" class="block mb-1">
          {{t "strategy_finder_0726.plan_copy.best_for_label"}}
        </Text>
        <Text @tag="p" @variant="muted">{{t @copy.bestFor}}</Text>
      </div>
      <AppPageProsVsCons @pros={{@copy.pros}} @cons={{@copy.cons}} />
      <div>
        <Text @weight="semibold" class="block mb-1">
          {{t "strategy_finder_0726.plan_copy.how_to_enroll"}}
        </Text>
        <ol class="list-decimal ml-4 space-y-1 app-rich-text">
          {{#each @copy.instructions as |step|}}
            <li><Text @variant="muted" @size="sm">{{t step htmlSafe=true}}</Text></li>
          {{/each}}
        </ol>
      </div>
    {{/if}}
  </div>
</template>;

interface PlanCopySignature {
  Args: {
    // The SF-0726 plan code (e.g. 'paye', 'ibr', 'repaymentAssistancePlan').
    plan: string;
    // Whether this row applies PSLF / a TLF amount, so the matching forgiveness
    // copy block is shown in addition to the base-plan copy.
    pslf?: boolean;
    // The PSLF balance forgiven (cents). When PSLF applies but forgives nothing
    // (the loan is repaid before the PSLF cutoff — "nothing left to forgive"),
    // the PSLF copy block is suppressed.
    pslfForgiveness?: number;
    tlfAmount?: number;
    // Optional plan name rendered as a heading above the toggle, so a standalone
    // disclosure (e.g. in the comparison table) says which plan it describes.
    // Omitted where the name is already shown alongside (e.g. an amortization
    // chart title).
    planName?: string;
  };
  Element: HTMLDivElement;
}

// The resolved copy blocks for a plan: the base-plan copy plus the PSLF and TLF
// overlay blocks when they apply. Split out from the disclosure below so the same
// content can be rendered inside the toggle's Collapsible (comparison table) or
// as a full-width table row (the per-loan results table).
interface PlanCopyContentSignature {
  Args: {
    plan: string;
    pslf?: boolean;
    pslfForgiveness?: number;
    tlfAmount?: number;
  };
  Element: HTMLDivElement;
}

export class PlanCopyContent extends Component<PlanCopyContentSignature> {
  get copy() {
    return planCopyKeys(this.args.plan);
  }

  get showPslf() {
    return !!this.args.pslf && (this.args.pslfForgiveness ?? 0) > 0;
  }

  get showTlf() {
    return (this.args.tlfAmount ?? 0) > 0;
  }

  pslfCopy = PSLF_COPY;
  tlfCopy = TLF_COPY;

  <template>
    <div class="space-y-4" ...attributes>
      <CopyBlock @copy={{this.copy}} />
      {{#if this.showPslf}}
        <CopyBlock
          @copy={{this.pslfCopy}}
          @heading="strategy_finder_0726.plan_copy.pslf_heading"
          data-test-sf-0726-plan-copy-pslf
        />
      {{/if}}
      {{#if this.showTlf}}
        <CopyBlock
          @copy={{this.tlfCopy}}
          @heading="strategy_finder_0726.plan_copy.tlf_heading"
          data-test-sf-0726-plan-copy-tlf
        />
      {{/if}}
    </div>
  </template>
}

// Collapsible "plan details" disclosure shown for the best, current, and
// compared plans in the comparison table. The descriptive copy lives entirely in
// front-end translations (see PLAN_COPY_KEYS in utils/strategy-finder-0726).
const StrategyFinder0726PlanCopy: TOC<PlanCopySignature> = <template>
  {{#let (uniqueId) (tracked false) as |bodyId isOpen|}}
    <div data-test-sf-0726-plan-copy={{@plan}} ...attributes>
      {{#if @planName}}
        <Text @weight="semibold" class="block" data-test-sf-0726-plan-copy-name>
          {{@planName}}
        </Text>
      {{/if}}
      <button
        type="button"
        class="tio-row gap-1 tio-fg-brand text-sm"
        aria-expanded={{if isOpen.value "true" "false"}}
        aria-controls={{bodyId}}
        data-test-sf-0726-plan-copy-toggle
        {{on "click" (fn isOpen.set (not isOpen.value))}}
      >
        {{t "strategy_finder_0726.plan_copy.details_toggle"}}
        <DisclosureToggle @expanded={{isOpen.value}} @bare={{true}} />
      </button>
      <Collapsible @isOpen={{isOpen.value}} id={{bodyId}} class="mt-2">
        <PlanCopyContent
          @plan={{@plan}}
          @pslf={{@pslf}}
          @pslfForgiveness={{@pslfForgiveness}}
          @tlfAmount={{@tlfAmount}}
        />
      </Collapsible>
    </div>
  {{/let}}
</template>;

export default StrategyFinder0726PlanCopy;
